import { useEffect, useRef } from "react";

/**
 * Run `onRefresh` every `intervalMs` ms while the tab is visible.
 *
 * - Pauses when the tab is hidden (no wasted polls / server load when the
 *   user is on another tab).
 * - When the tab regains focus, if more than `intervalMs` has elapsed since
 *   the last call, fires immediately — so the user sees fresh data the
 *   moment they switch back, not after waiting another full interval.
 * - Uses a callback ref so the latest `onRefresh` is invoked without
 *   tearing down the interval on every render.
 */
export function useAutoRefresh(onRefresh, { intervalMs = 60_000, enabled = true } = {}) {
  const lastFiredAt = useRef(Date.now());
  const cbRef = useRef(onRefresh);
  cbRef.current = onRefresh;

  useEffect(() => {
    if (!enabled) return undefined;

    const tick = () => {
      if (document.visibilityState !== "visible") return;
      lastFiredAt.current = Date.now();
      cbRef.current();
    };

    const intervalId = setInterval(tick, intervalMs);

    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      const elapsed = Date.now() - lastFiredAt.current;
      if (elapsed >= intervalMs) tick();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [intervalMs, enabled]);
}
